// components/Headers.jsx — Phase F premium marketplace shell.
//
// Behavior preserved verbatim from the previous implementation:
//   - Site-settings fetch
//   - Click-outside handlers (search, notifications)
//   - Socket initialisation + `notification:new` listener + ping sound
//   - get_card_products / get_wishlist_products / get_user_balance on login
//   - query_products dispatch on debounced search
//   - SELLER_ONLY_NOTIFICATION_TYPES filter

import React, { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import {
    FaSearch, FaBell, FaWallet, FaLock, FaStore, FaHeadset,
    FaList, FaFacebookF, FaLinkedinIn, FaGithub, FaInstagram, FaYoutube,
    FaThLarge, FaShoppingBag, FaFire, FaGift, FaChartLine,
    FaInfoCircle, FaBlog, FaEnvelope, FaComments,
} from 'react-icons/fa'
import { AiOutlineTwitter, AiFillHeart, AiFillShopping } from 'react-icons/ai'
import { MdClose } from 'react-icons/md'
import { SiTiktok } from 'react-icons/si'
import { IoIosCall } from 'react-icons/io'

import api from '../api/api'
import { setFavicon, resolveSiteIconUrl } from '../utils/favicon'
import { initSocket } from '../utilies/config'
import { getHeaderSection } from '../utilies/pageMeta'

import { get_card_products, get_wishlist_products } from '../store/reducers/cardReducer'
import { get_user_balance } from '../store/reducers/balanceReducer'
import { query_products } from '../store/reducers/homeReducer'
import {
    fetch_notifications, fetch_unread_count,
    add_realtime_notification, reset_notifications,
} from '../store/reducers/notificationReducer'
import { fetch_chat_unread_count, resetChatUnread } from '../store/reducers/chatReducer'

import SearchPanel from './header/SearchPanel'
import NotificationPanel from './header/NotificationPanel'
import DesktopCategoryMegaMenu from './header/DesktopCategoryMegaMenu'
import LanguageSwitcher from './LanguageSwitcher'
import AvatarBadge from './customer/AvatarBadge'
import { isLiveSearchQuery, rankSearchResults } from '../utilies/searchRanking'
// ─── helpers (preserved) ─────────────────────────────────────
const SOCIAL_ICON = {
    facebook:  <FaFacebookF />,
    twitter:   <AiOutlineTwitter />,
    linkedin:  <FaLinkedinIn />,
    github:    <FaGithub />,
    instagram: <FaInstagram />,
    youtube:   <FaYoutube />,
    tiktok:    <SiTiktok />,
}

const isUsableUrl = (url) => {
    if (!url) return false
    const t = String(url).trim()
    return t && t !== '#' && !/^javascript:/i.test(t)
}

const playNotifSound = () => {
    try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)()
        const osc = ctx.createOscillator(); const gain = ctx.createGain()
        osc.connect(gain); gain.connect(ctx.destination)
        osc.frequency.setValueAtTime(880, ctx.currentTime)
        osc.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.1)
        gain.gain.setValueAtTime(0.3, ctx.currentTime)
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3)
        osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.3)
    } catch (_) {}
}

const useDebounce = (value, delay = 350) => {
    const [dv, setDv] = useState(value)
    useEffect(() => {
        const t = setTimeout(() => setDv(value), delay)
        return () => clearTimeout(t)
    }, [value, delay])
    return dv
}

const SELLER_ONLY_NOTIFICATION_TYPES = [
    'doping_activated', 'doping_expired', 'new_order_received',
    'delivery_confirmed_by_customer', 'seller_balance_credited', 'seller_credit_released',
]

const normalizeRole = (role) => {
    const n = (role || '').toString().toLowerCase()
    if (n === 'user' || n === 'client') return 'customer'
    return n || 'customer'
}

const notificationBelongsToUser = (payload, userInfo) => {
    const role = normalizeRole(userInfo?.role || 'customer')
    const userId = userInfo?.id || userInfo?._id
    if (!payload || !userId) return false
    const notificationType = payload?.type || payload?.notifType
    if (role === 'customer' && SELLER_ONLY_NOTIFICATION_TYPES.includes(notificationType)) return false
    if (role === 'admin') return payload.recipientType === 'admin'
    return payload.recipientType === role && payload.recipientId?.toString() === userId?.toString()
}

// ─── default site settings ───────────────────────────────────
const DEFAULT_SETTINGS = {
    logoUrl:        '/images/logo.png',
    topBarSlogan:   'Rəqəmsal məhsullar bazarı',
    supportPhone:   '+994 50 730 56 79',
    supportLabel:   'Dəstək xidməti',
    headerSocials: [
        { platform: 'facebook',  url: '', active: true },
        { platform: 'instagram', url: '', active: true },
        { platform: 'twitter',   url: '', active: true },
        { platform: 'youtube',   url: '', active: true },
    ],
    navLinks: [
        { label: 'Ana səhifə', path: '/',         active: true },
        { label: 'Bazar',      path: '/market',   active: true },
        { label: 'Mağazalar',  path: '/magazalar', active: true },
        { label: 'Blog',       path: '/blogs',    active: true },
        { label: 'Rəylər',     path: '/reviews',  active: true },
    ],
}

const PUBLIC_MARKETPLACE_NAV = [
    { label: 'Kateqoriyalar', path: '/kateqoriyalar', icon: <FaThLarge />, group: 'main' },
    { label: 'Elan bazarı', path: '/bazar', icon: <FaShoppingBag />, group: 'main' },
    { label: 'Mağazalar', path: '/magazalar', icon: <FaStore />, group: 'main' },
    { label: 'Həftənin fürsətləri', path: '/haftanin-firsatlari', icon: <FaFire />, group: 'main' },
    { label: 'Çəkilişlər', path: '/cekilisler', icon: <FaGift />, group: 'main' },
    { label: 'Axtarış vitrini', path: '/axtaris-vitrini', icon: <FaChartLine />, group: 'main' },
    { label: 'Haqqımızda', path: '/About', icon: <FaInfoCircle />, group: 'secondary' },
    { label: 'Blog', path: '/blogs', icon: <FaBlog />, group: 'secondary' },
    { label: 'Əlaqə', path: '/contact', icon: <FaEnvelope />, group: 'secondary' },
    { label: 'Dəstək', path: '/support', icon: <FaHeadset />, group: 'secondary' }
]

// ─── Nav label translation (Phase I) ─────────────────────────
// The backend `/site-settings` response can override `navLinks` with English
// labels (Home / Shop / About / Contact …) which used to cause a visible
// flicker — AZ first paint, then English after the API merge.
//
// Strategy: pin the visible label to the AZ string by route path. The
// backend can still configure which links appear and their order; we only
// override the rendered text. Unknown paths fall back to whatever label
// the backend sent (so future routes still render).
const NAV_LABEL_BY_PATH = {
    '/':         'Ana səhifə',
    '/market':   'Bazar',
    '/magazalar':'Mağazalar',
    '/shops':    'Mağazalar',
    '/stores':   'Mağazalar',
    '/blogs':    'Blog',
    '/blog':     'Blog',
    '/official': 'Rəsmi məhsullar',
    '/reviews':  'Rəylər',
    '/site-reviews': 'Rəylər',
    '/about':    'Haqqımızda',
    '/About':    'Haqqımızda',
    '/contact':  'Əlaqə',
    '/Contact':  'Əlaqə',
    '/fag':      'Tez-tez verilən suallar',
    '/faq':      'Tez-tez verilən suallar',
    '/cekilisler': 'Çəkilişlər',
    '/cekilis':    'Çəkilişlər',
    '/kampaniyalar': 'Kampaniyalar',
    '/kampaniya':    'Kampaniyalar',
}

// Normalise a path (strip trailing slash, lowercase keys we know).
const normalizeNavPath = (p) => {
    if (!p || typeof p !== 'string') return ''
    const trimmed = p.trim()
    if (trimmed === '/') return '/'
    return trimmed.replace(/\/+$/, '')
}

const azLabelFor = (link) => {
    const p = normalizeNavPath(link?.path)
    if (NAV_LABEL_BY_PATH[p]) return NAV_LABEL_BY_PATH[p]
    // Try a lowercase lookup for path-case mismatches.
    const lowered = p.toLowerCase()
    if (NAV_LABEL_BY_PATH[lowered]) return NAV_LABEL_BY_PATH[lowered]
    return link?.label || ''
}

// ─── Nav label i18n (Phase 1) ────────────────────────────────
// Maps the same known routes to a `common.nav.*` key. Unknown paths fall
// back to azLabelFor() (backend label / AZ pin), so nothing ever renders
// blank. AZ values are identical to NAV_LABEL_BY_PATH → no AZ-mode change.
const NAV_KEY_BY_PATH = {
    '/':             'nav.home',
    '/market':       'nav.market',
    '/magazalar':    'nav.shops',
    '/shops':        'nav.shops',
    '/stores':       'nav.shops',
    '/blogs':        'nav.blog',
    '/blog':         'nav.blog',
    '/official':     'nav.official',
    '/reviews':      'nav.reviews',
    '/site-reviews': 'nav.reviews',
    '/about':        'nav.about',
    '/contact':      'nav.contact',
    '/fag':          'nav.faq',
    '/faq':          'nav.faq',
    '/cekilisler':   'nav.giveaways',
    '/cekilis':      'nav.giveaways',
    '/kampaniyalar': 'nav.campaigns',
    '/kampaniya':    'nav.campaigns',
}

const navKeyFor = (link) => {
    const p = normalizeNavPath(link?.path)
    return NAV_KEY_BY_PATH[p] || NAV_KEY_BY_PATH[p.toLowerCase()] || null
}

// ─── Nav active-state (route consistency fix) ────────────────
// The active nav item is derived ONLY from location.pathname, never from
// click state or whatever exact path the backend configured for a link.
// Both the current URL and each link's path resolve to a nav "group key";
// a link is active when the groups match. This keeps the highlight stable
// across refresh, direct URL open and client-side navigation.
const getActiveNavKey = (rawPath) => {
    const p = normalizeNavPath(rawPath).toLowerCase()
    if (p === '' || p === '/') return 'home'
    if (
        p.startsWith('/market') || p.startsWith('/bazar') ||
        p.startsWith('/products') || p.startsWith('/product') ||
        p.startsWith('/search') || p.startsWith('/category') ||
        p.startsWith('/haftanin-firsatlari') || p.startsWith('/vitrin-mehsullari')
    ) return 'market'
    if (
        p.startsWith('/magazalar') || p.startsWith('/shops') ||
        p.startsWith('/shop/') || p.startsWith('/shop') ||
        p.startsWith('/stores') || p.startsWith('/seller/shop')
    ) return 'shops'
    if (p.startsWith('/blog')) return 'blog'
    if (p.startsWith('/reviews') || p.startsWith('/site-reviews')) return 'reviews'
    if (p.startsWith('/about')) return 'about'
    if (p.startsWith('/contact')) return 'contact'
    if (p.startsWith('/fag') || p.startsWith('/faq')) return 'faq'
    if (p.startsWith('/cekilis')) return 'giveaways'
    if (p.startsWith('/kampaniya')) return 'campaigns'
    if (p.startsWith('/official')) return 'official'
    return null
}

// Canonical destination per nav group — a backend-configured link must never
// send "Mağazalar" to the legacy /shops product grid (or similar aliases).
const NAV_CANONICAL_PATH = {
    '/shops':  '/magazalar',
    '/stores': '/magazalar',
}

const navLinkPath = (link) => {
    const p = normalizeNavPath(link?.path)
    return NAV_CANONICAL_PATH[p.toLowerCase()] || link?.path || '/'
}

// ─── reusable icon button ────────────────────────────────────
const IconAction = React.forwardRef(function IconAction(
    { icon, count, label, labelText, onClick, className = '' },
    ref
) {
    return (
        <button
            ref={ref}
            type='button'
            onClick={onClick}
            aria-label={label}
            className={`relative inline-flex h-10 items-center justify-center gap-2 rounded-card border border-slate-200 bg-white px-3 text-slate-700 transition-colors hover:border-brand-400 hover:text-brand-700 ${labelText ? '' : 'w-10 px-0'} ${className}`}
        >
            {icon}
            {labelText && <span className='text-sm font-semibold md-lg:hidden'>{labelText}</span>}
            {typeof count === 'number' && count > 0 && (
                <span className='absolute -right-1.5 -top-1.5 inline-flex min-w-[18px] h-[18px] items-center justify-center rounded-pill bg-brand-600 px-1 text-[10px] font-bold text-white'>
                    {count > 99 ? '99+' : count}
                </span>
            )}
        </button>
    )
})

// ─── main component ──────────────────────────────────────────
const Headers = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { pathname } = useLocation()
    const activeNavKey = getActiveNavKey(pathname)
    const { t } = useTranslation()

    // Visible nav label: translated when the route is known, otherwise the
    // backend/AZ label (so admin-configured custom links still render).
    const navLabel = (link) => {
        const key = navKeyFor(link)
        return key ? t(key, { defaultValue: azLabelFor(link) }) : azLabelFor(link)
    }

    const { categorys, products: storeProducts, premiumProducts: storePremiumProducts } = useSelector(s => s.home)
    const liveSearchProducts = rankSearchResults(storePremiumProducts || [], storeProducts || [], 4).slice(0, 10)
    const { userInfo }                            = useSelector(s => s.auth)
    const { card_product_count, wishlist_count }  = useSelector(s => s.card)
    const { balance }                             = useSelector(s => s.balance)
    const { unreadCount }                         = useSelector(s => s.notification)
    const { unreadCount: chatUnreadCount }        = useSelector(s => s.chat)

    const [siteSettings, setSiteSettings]   = useState(DEFAULT_SETTINGS)
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
    const [searchValue, setSearchValue]     = useState('')
    const [searchCategory, setSearchCategory] = useState('')
    const [dropdownVisible, setDropdownVisible] = useState(false)
    const [searchLoading, setSearchLoading]     = useState(false)
    const [searchShowcaseProducts, setSearchShowcaseProducts] = useState([])
    const [searchShowcaseLoading, setSearchShowcaseLoading] = useState(false)
    const [searchShowcaseLoaded, setSearchShowcaseLoaded] = useState(false)
    const [notifVisible, setNotifVisible]   = useState(false)
    const [notifLoaded, setNotifLoaded]     = useState(false)
    const [categoryMegaOpen, setCategoryMegaOpen] = useState(false)

    const notifWrapperRef  = useRef(null)
    const socketRef        = useRef(null)
    const debouncedQuery   = useDebounce(searchValue, 350)

    // Site settings
    useEffect(() => {
        api.get('/site-settings').then(({ data }) => {
            if (data.success) {
                setSiteSettings(prev => ({ ...prev, ...data.data }))
                // ✅ Admin-uploaded site icon → browser favicon (fallback: default).
                const iconUrl = resolveSiteIconUrl(data.data)
                if (iconUrl) setFavicon(iconUrl)
            }
        }).catch(() => {})
    }, [])

    // Click-outside — search uses a data-attribute selector so multiple
    // wrappers (desktop + mobile rows) all work without ref collisions.
    useEffect(() => {
        const onDoc = (e) => {
            if (!e.target.closest?.('[data-search-area]')) setDropdownVisible(false)
            if (notifWrapperRef.current && !notifWrapperRef.current.contains(e.target)) setNotifVisible(false)
            if (!e.target.closest?.('[data-category-mega-area]')) setCategoryMegaOpen(false)
        }
        const onKey = (e) => {
            if (e.key === 'Escape') {
                setDropdownVisible(false)
                setCategoryMegaOpen(false)
            }
        }
        document.addEventListener('mousedown', onDoc)
        document.addEventListener('keydown', onKey)
        return () => {
            document.removeEventListener('mousedown', onDoc)
            document.removeEventListener('keydown', onKey)
        }
    }, [])

    useEffect(() => { setCategoryMegaOpen(false) }, [pathname])

    // Socket
    useEffect(() => {
        if (!userInfo) return
        dispatch(fetch_unread_count())
        dispatch(fetch_chat_unread_count())
        const sock = initSocket()
        socketRef.current = sock
        sock.on('connect', () => sock.emit('add_user', userInfo.id, userInfo))
        const handleNotification = (notif) => {
            if (!notificationBelongsToUser(notif, userInfo)) return
            dispatch(add_realtime_notification(notif))
            if ((notif?.type || notif?.notifType) === 'chat_message_received') {
                dispatch(fetch_chat_unread_count())
            }
            setNotifLoaded(false)
            playNotifSound()
        }
        sock.on('notification:new', handleNotification)
        return () => {
            sock.off('notification:new', handleNotification)
            socketRef.current = null
        }
    }, [userInfo, dispatch])

    useEffect(() => {
        if (!userInfo) dispatch(resetChatUnread())
    }, [userInfo, dispatch])

    const handleBellClick = () => {
        const next = !notifVisible
        setNotifVisible(next)
        if (next && !notifLoaded) {
            dispatch(reset_notifications())
            dispatch(fetch_notifications({ page: 1, limit: 20 }))
            setNotifLoaded(true)
        }
    }

    // Live search via debounced query (preserves existing query_products dispatch)
    useEffect(() => {
        if (!isLiveSearchQuery(debouncedQuery)) return
        setSearchLoading(true)
        const request = dispatch(query_products({
            low: '', high: '', category: searchCategory || '',
            rating: '', sortPrice: '', pageNumber: 1,
            searchValue: debouncedQuery.trim(), limit: 10,
        }))
        request.finally(() => setSearchLoading(false))
    }, [debouncedQuery, searchCategory, dispatch])

    const submitSearch = () => {
        if (!searchValue.trim()) return
        setDropdownVisible(false)
        const params = new URLSearchParams()
        if (searchCategory) params.set('category', searchCategory)
        params.set('value', searchValue.trim())
        navigate(`/products/search?${params.toString()}`)
    }

    const openSearchPanel = () => {
        setDropdownVisible(true)
        if (searchShowcaseLoaded || searchShowcaseLoading) return
        setSearchShowcaseLoading(true)
        api.get('/home/query-products', { params: { type: 'search_showcase', page: 1, limit: 10 } })
            .then(({ data }) => setSearchShowcaseProducts(Array.isArray(data?.products) ? data.products : []))
            .catch(() => setSearchShowcaseProducts([]))
            .finally(() => {
                setSearchShowcaseLoading(false)
                setSearchShowcaseLoaded(true)
            })
    }

    const runSearch = (keyword) => {
        const value = String(keyword || '').trim()
        if (!value) return
        setSearchValue(value)
        setDropdownVisible(false)
        const params = new URLSearchParams()
        if (searchCategory) params.set('category', searchCategory)
        params.set('value', value)
        navigate(`/products/search?${params.toString()}`)
    }

    const openCategory = (category) => {
        const value = category?.name || category?.title || ''
        if (!value) return
        setDropdownVisible(false)
        navigate(`/products?category=${encodeURIComponent(value)}`)
    }

    const handleSearchKeyDown = (e) => {
        if (e.key === 'Enter') submitSearch()
        if (e.key === 'Escape') setDropdownVisible(false)
    }

    // Eager loads when user logs in
    useEffect(() => {
        if (userInfo) {
            dispatch(get_card_products(userInfo.id))
            dispatch(get_wishlist_products(userInfo.id))
            dispatch(get_user_balance(userInfo.id))
        }
    }, [userInfo, dispatch])

    const activeNavLinks    = [
        ...(siteSettings.navLinks || [])
            .filter(l => l.active).sort((a, b) => (a.order || 0) - (b.order || 0)),
        // ✅ Rəsmi mağaza həmişə əlçatandır (backend konfiqindən asılı deyil)
        // { label: 'Rəsmi məhsullar', path: '/official', active: true },
    ]
    const activeHeaderSocials = (siteSettings.headerSocials || [])
        .filter(s => s.active && isUsableUrl(s.url))

    const goCart       = () => navigate(userInfo ? '/card' : '/login')
    const goWishlist   = () => navigate(userInfo ? '/dashboard/my-wishlist' : '/login')
    const goChat       = () => navigate(userInfo ? '/dashboard/chat' : '/login')
    const goAccount    = () => navigate(userInfo ? '/dashboard' : '/login')
    const goBalance    = () => navigate('/dashboard/my-balance')
    const closeMobile  = () => setMobileMenuOpen(false)
    const isSeller     = normalizeRole(userInfo?.role) === 'seller'

    return (
        <header className='relative w-full'>
            {/* ───── DESKTOP TOP UTILITY BAR ───── */}
            <div className='block md-lg:hidden bg-slate-50 border-b border-slate-200 text-xs'>
                <div className='mx-auto flex max-w-marketplace items-center justify-between gap-4 px-4 md-lg:px-5 lg:px-8 h-9'>
                    <p className='text-slate-500'>
                        <span className='font-semibold text-slate-700'>Itemzo</span>
                        <span className='mx-1.5 text-slate-300'>·</span>
                        ItemZo — Oyunlar, Gift Card & Abunəliklər
                    </p>
                    <div className='flex items-center gap-4 text-slate-500'>
                        <Link to='/fag' className='hover:text-brand-700'>Yardım</Link>
                        <Link to='/satici-ol' className='font-semibold text-brand-700 hover:text-brand-800'>Satıcı ol</Link>
                        <Link to='/magazalar' className='hover:text-brand-700'>Mağazalar</Link>
                        <Link to='/support' className='hover:text-brand-700'>Dəstək</Link>
                        {!userInfo && (
                            <Link to='/login' className='inline-flex items-center gap-1 text-slate-600 hover:text-brand-700 transition-colors'>
                                <FaLock size={11} />
                                {t('topbar.login')}
                            </Link>
                        )}
                        <LanguageSwitcher className='ml-1' />
                    </div>
                </div>
            </div>

            {/* ───── MAIN ROW (sticky) ───── */}
            <div className='sticky top-0 z-30 bg-white border-b border-slate-200'>
                <div className='mx-auto flex max-w-marketplace items-center gap-5 md-lg:gap-3 px-4 md-lg:px-5 lg:px-8 h-[68px] md-lg:h-14'>
                    {/* Mobile hamburger */}
                    <button
                        type='button'
                        onClick={() => setMobileMenuOpen(true)}
                        aria-label={t('topbar.openMenu')}
                        className='hidden md-lg:inline-flex h-10 w-10 items-center justify-center rounded-card border border-slate-200 bg-white text-slate-700 hover:border-brand-400 hover:text-brand-700 transition-colors'
                    >
                        <FaList size={14} />
                    </button>

                    {/* Logo + small tagline (desktop) */}
                    <Link
                        to='/'
                        aria-label='Ana səhifə — ItemZo'
                        className='shrink-0 inline-flex items-center gap-3 md-lg:mx-auto'
                    >
                        <img
                            src={siteSettings.logoUrl}
                            alt='ItemZo'
                            onError={e => e.currentTarget.src = '/images/logo.png'}
                            className='h-10 md-lg:h-9 w-auto object-contain transition-transform duration-150 hover:scale-[1.02]'
                        />
                        <span className='inline-flex xl:hidden flex-col leading-tight border-l border-slate-200 pl-3 -mb-0.5'>
                            <span className='text-[10px] font-bold uppercase tracking-wider text-brand-700'>
                                Marketplace
                            </span>
                            <span className='text-[11px] text-slate-500'>
                                {getHeaderSection(pathname)}
                            </span>
                        </span>
                    </Link>

                    {/* Desktop search — visual center */}
                    <div data-search-area className='relative mx-auto flex-1 max-w-3xl md-lg:hidden'>
                        <div className='relative flex h-11 items-center overflow-hidden rounded-card border border-slate-200 bg-white focus-within:border-brand-500 focus-within:shadow-ring transition-colors'>
                            <span className='pl-3 text-slate-400'>
                                <FaSearch size={14} />
                            </span>
                            <input
                                value={searchValue}
                                onChange={e => { setSearchValue(e.target.value); setDropdownVisible(true) }}
                                onFocus={openSearchPanel}
                                onKeyDown={handleSearchKeyDown}
                                type='text'
                                placeholder={t('search.placeholderDesktop')}
                                aria-label={t('search.aria')}
                                className='h-full flex-1 bg-transparent px-3 text-sm text-slate-800 outline-none placeholder:text-slate-400'
                            />
                            {searchValue && (
                                <button
                                    type='button'
                                    onClick={() => { setSearchValue(''); openSearchPanel() }}
                                    aria-label={t('search.clear')}
                                    className='inline-flex h-7 w-7 mr-1 items-center justify-center rounded-pill text-slate-400 hover:bg-slate-100'
                                >
                                    <MdClose size={14} />
                                </button>
                            )}
                            <button
                                type='button'
                                onClick={submitSearch}
                                aria-label={t('search.submit')}
                                className='inline-flex h-full w-11 items-center justify-center bg-brand-600 text-white hover:bg-brand-700 transition-colors'
                            >
                                <FaSearch size={14} />
                            </button>
                        </div>
                        <SearchPanel
                            query={searchValue}
                            visible={dropdownVisible}
                            products={liveSearchProducts}
                            loading={searchLoading}
                            categories={categorys}
                            showcaseProducts={searchShowcaseProducts}
                            showcaseLoading={searchShowcaseLoading}
                            onSelect={() => setDropdownVisible(false)}
                            onPopularSearch={runSearch}
                            onCategory={openCategory}
                        />
                    </div>

                    {/* Right cluster */}
                    <div className='ml-auto flex items-center gap-2'>
                        {/* Balance pill — desktop only */}
                        {userInfo && (
                            <button
                                type='button'
                                onClick={goBalance}
                                aria-label={t('account.balance')}
                                className='inline-flex md-lg:hidden h-10 items-center gap-2 rounded-card border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 hover:border-brand-400 hover:text-brand-700 transition-colors'
                            >
                                <FaWallet size={13} className='text-success-600' />
                                {Number(balance || 0).toFixed(2)} ₼
                            </button>
                        )}

                        {/* Chat */}
                        <IconAction
                            icon={<FaComments size={16} className='text-brand-600' />}
                            count={userInfo ? chatUnreadCount : 0}
                            onClick={goChat}
                            label='Çat'
                        />

                        {/* Notification */}
                        {userInfo && (
                            <div ref={notifWrapperRef} className='relative'>
                                <IconAction
                                    icon={<FaBell size={15} />}
                                    count={unreadCount}
                                    onClick={handleBellClick}
                                    label={t('account.notifications')}
                                />
                                <NotificationPanel
                                    visible={notifVisible}
                                    onClose={() => setNotifVisible(false)}
                                    belongsToUser={notificationBelongsToUser}
                                />
                            </div>
                        )}

                        {/* Wishlist — desktop only */}
                        <IconAction
                            icon={<AiFillHeart size={16} className='text-danger-500' />}
                            count={wishlist_count}
                            onClick={goWishlist}
                            label={t('account.wishlist')}
                            className='md-lg:hidden'
                        />

                        {/* Cart */}
                        <IconAction
                            icon={<AiFillShopping size={16} className='text-accent-500' />}
                            count={card_product_count}
                            onClick={goCart}
                            label={t('account.cart')}
                        />

                        {/* Profile — desktop only */}
                        <button
                            type='button'
                            onClick={goAccount}
                            aria-label={userInfo ? t('account.myAccount') : t('account.login')}
                            className='inline-flex md-lg:hidden h-10 items-center gap-2 rounded-card border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 hover:border-brand-400 hover:text-brand-700 transition-colors'
                        >
                            {userInfo ? <AvatarBadge avatarId={userInfo.avatarId} size='xs' /> : <FaLock size={13} />}
                            <span>{userInfo ? t('account.myAccount') : t('account.login')}</span>
                        </button>
                    </div>
                </div>

                {/* ───── Mobile search row (sticks with main row) ───── */}
                <div className='hidden md-lg:block border-t border-slate-100 px-4 py-2.5'>
                    <div data-search-area className='relative'>
                        <div className='relative flex h-11 items-center overflow-hidden rounded-card border border-slate-200 bg-white focus-within:border-brand-500 focus-within:shadow-ring transition-colors'>
                            <span className='pl-3 text-slate-400'>
                                <FaSearch size={14} />
                            </span>
                            <input
                                value={searchValue}
                                onChange={e => { setSearchValue(e.target.value); setDropdownVisible(true) }}
                                onFocus={openSearchPanel}
                                onKeyDown={handleSearchKeyDown}
                                type='text'
                                placeholder={t('search.placeholderMobile')}
                                aria-label={t('search.aria')}
                                className='h-full flex-1 bg-transparent px-3 text-sm text-slate-800 outline-none placeholder:text-slate-400'
                            />
                            {searchValue && (
                                <button
                                    type='button'
                                    onClick={() => { setSearchValue(''); openSearchPanel() }}
                                    aria-label={t('search.clear')}
                                    className='inline-flex h-7 w-7 mr-1 items-center justify-center rounded-pill text-slate-400 hover:bg-slate-100'
                                >
                                    <MdClose size={14} />
                                </button>
                            )}
                            <button
                                type='button'
                                onClick={submitSearch}
                                aria-label={t('search.submit')}
                                className='inline-flex h-full w-11 items-center justify-center bg-brand-600 text-white hover:bg-brand-700 transition-colors'
                            >
                                <FaSearch size={14} />
                            </button>
                        </div>
                        <SearchPanel
                            query={searchValue}
                            visible={dropdownVisible}
                            products={liveSearchProducts}
                            loading={searchLoading}
                            categories={categorys}
                            showcaseProducts={searchShowcaseProducts}
                            showcaseLoading={searchShowcaseLoading}
                            onSelect={() => setDropdownVisible(false)}
                            onPopularSearch={runSearch}
                            onCategory={openCategory}
                        />
                    </div>
                </div>

                <nav className='mx-auto flex h-11 max-w-marketplace items-center gap-1 border-t border-slate-100 px-4 md-lg:hidden lg:px-8' aria-label='Marketplace naviqasiyası'>
                    {PUBLIC_MARKETPLACE_NAV.filter(item => item.path !== '/support').map(({ label, path, icon }) => path === '/kateqoriyalar' ? (
                        <div key={path} data-category-mega-area>
                            <button type='button' onClick={() => setCategoryMegaOpen(open => !open)} aria-expanded={categoryMegaOpen} aria-controls='desktop-category-mega-menu' className={`inline-flex items-center gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-bold transition-colors ${pathname.toLowerCase().startsWith('/kateqori') || categoryMegaOpen ? 'bg-brand-50 text-brand-700' : 'text-slate-600 hover:bg-slate-50 hover:text-brand-700'}`}>
                                <span className='text-[12px]'>{icon}</span>{label}
                            </button>
                        </div>
                    ) : (
                        <Link key={path} to={path} className={`inline-flex items-center gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-bold transition-colors ${pathname.toLowerCase() === path.split('?')[0].toLowerCase() ? 'bg-brand-50 text-brand-700' : 'text-slate-600 hover:bg-slate-50 hover:text-brand-700'}`}>
                            <span className='text-[12px]'>{icon}</span>{label}
                        </Link>
                    ))}
                </nav>
                <div data-category-mega-area>
                    <DesktopCategoryMegaMenu open={categoryMegaOpen} onClose={() => setCategoryMegaOpen(false)} />
                </div>
            </div>

            {/* ───── MOBILE SIDE DRAWER ───── */}
            <div
                className={`fixed inset-0 z-[9999] transition-opacity duration-200 hidden md-lg:block ${
                    mobileMenuOpen ? 'visible bg-black/50' : 'invisible bg-transparent'
                }`}
                onClick={closeMobile}
                aria-hidden={!mobileMenuOpen}
            />
            <aside
                className={`fixed left-0 top-0 z-[9999] h-screen w-[min(340px,92vw)] max-w-full overflow-x-hidden overflow-y-auto overscroll-contain bg-white px-4 pb-28 pt-4 box-border transition-transform duration-200 hidden md-lg:block shadow-card-hover sm:px-5 sm:pt-5 ${
                    mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
                }`}
                aria-label={t('topbar.mobileMenu')}
            >
                <div className='sticky top-0 z-10 -mx-1 mb-5 flex items-center justify-between bg-white/95 px-1 py-1 backdrop-blur'>
                    <Link to='/' onClick={closeMobile} className='inline-flex items-center gap-2'>
                        <img
                            src={siteSettings.logoUrl}
                            alt='ItemZo'
                            onError={e => e.currentTarget.src = '/images/logo.png'}
                            className='h-9 w-auto object-contain'
                        />
                    </Link>
                    <button
                        type='button'
                        onClick={closeMobile}
                        aria-label={t('topbar.close')}
                        className='inline-flex h-9 w-9 items-center justify-center rounded-card border border-slate-200 bg-white text-slate-500 hover:border-brand-400'
                    >
                        <MdClose size={16} />
                    </button>
                </div>

                {/* Language switcher (mobile drawer) */}
                <div className='mb-4'>
                    <LanguageSwitcher />
                </div>

                {userInfo && (
                    <button
                        type='button'
                        onClick={() => { closeMobile(); goBalance() }}
                        className='mb-3 flex w-full items-center justify-between gap-3 rounded-card border border-slate-200 bg-white px-4 py-3 hover:border-brand-300 transition-colors'
                    >
                        <span className='inline-flex items-center gap-2 text-slate-700'>
                            <FaWallet className='text-success-600' />
                            <span className='font-semibold text-sm'>{t('account.balance')}</span>
                        </span>
                        <span className='text-sm font-bold text-slate-900'>
                            {Number(balance || 0).toFixed(2)} ₼
                        </span>
                    </button>
                )}

                {userInfo ? (
                    <Link
                        to='/dashboard'
                        onClick={closeMobile}
                        className='mb-3 flex items-center gap-3 rounded-card border border-slate-200 bg-white px-4 py-3 hover:border-brand-300'
                    >
                        <AvatarBadge avatarId={userInfo.avatarId} size='sm' />
                        <div className='min-w-0'>
                            <p className='truncate text-sm font-bold text-slate-800'>{userInfo.name || t('account.myAccount')}</p>
                            <p className='text-xs text-slate-500'>{t('account.panel')}</p>
                        </div>
                    </Link>
                ) : (
                    <Link
                        to='/login'
                        onClick={closeMobile}
                        className='mb-3 flex items-center justify-center gap-2 rounded-card bg-brand-600 px-4 py-3 text-sm font-bold text-white hover:bg-brand-700'
                    >
                        <FaLock /> {t('account.loginCta')}
                    </Link>
                )}

                <Link
                    to={isSeller ? '/seller/dashboard' : '/satici-ol'}
                    onClick={closeMobile}
                    className='mb-5 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-brand-600 to-violet-600 px-4 py-3 text-sm font-black text-white shadow-sm transition hover:from-brand-700 hover:to-violet-700'
                >
                    <FaStore size={14} /> {isSeller ? 'Satıcı paneli' : 'Satıcı ol'}
                </Link>

                <nav className='mb-6 min-w-0' aria-label={t('topbar.ariaMobile')}>
                    <p className='mb-3 px-1 text-[10px] font-black uppercase tracking-[0.16em] text-slate-400'>Marketplace</p>
                    <div className='grid min-w-0 gap-2.5' style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))' }}>
                        {PUBLIC_MARKETPLACE_NAV.filter(item => item.group === 'main').map(({ label, path, icon }) => {
                            const active = pathname.toLowerCase() === path.toLowerCase() || pathname.toLowerCase().startsWith(`${path.toLowerCase()}/`)
                            return <Link key={path} to={path} onClick={closeMobile} className={`flex h-[104px] min-w-0 flex-col items-start justify-between rounded-2xl border p-3 text-left text-xs font-bold leading-4 transition-colors active:scale-[0.98] ${active ? 'border-brand-300 bg-brand-50 text-brand-700 shadow-sm' : 'border-slate-200 bg-white text-slate-700 hover:border-brand-200 hover:bg-brand-50/50'}`}><span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-sm ${active ? 'bg-white text-brand-700 shadow-sm' : 'bg-slate-50 text-slate-500'}`}>{icon}</span><span className='line-clamp-2 w-full min-w-0 break-words'>{label}</span></Link>
                        })}
                    </div>
                    <p className='mb-3 mt-7 px-1 text-[10px] font-black uppercase tracking-[0.16em] text-slate-400'>Məlumat və dəstək</p>
                    <div className='flex min-w-0 flex-col gap-1.5'>
                        {PUBLIC_MARKETPLACE_NAV.filter(item => item.group === 'secondary').map(({ label, path, icon }) => {
                            const active = pathname.toLowerCase() === path.toLowerCase() || pathname.toLowerCase().startsWith(`${path.toLowerCase()}/`)
                            return <Link key={path} to={path} onClick={closeMobile} className={`flex min-h-[44px] min-w-0 items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors ${active ? 'bg-brand-50 text-brand-700' : 'text-slate-700 hover:bg-slate-50'}`}><span className='flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-50 text-xs text-slate-500'>{icon}</span><span className='min-w-0 break-words'>{label}</span></Link>
                        })}
                    </div>
                </nav>

                {/* Support */}
                <div className='border-t border-slate-100 pt-4 mb-3 flex items-center gap-3'>
                    <span className='inline-flex h-10 w-10 items-center justify-center rounded-card bg-brand-50 text-brand-700'>
                        <IoIosCall />
                    </span>
                    <div className='min-w-0'>
                        <p className='text-sm font-semibold text-slate-800 truncate'>{siteSettings.supportPhone}</p>
                        <p className='text-xs text-slate-500'>{siteSettings.supportLabel}</p>
                    </div>
                </div>

                {activeHeaderSocials.length > 0 && (
                    <ul className='flex items-center gap-2'>
                        {activeHeaderSocials.map((s, i) => (
                            <li key={i}>
                                <a
                                    href={s.url}
                                    target='_blank'
                                    rel='noreferrer'
                                    aria-label={s.platform}
                                    className='inline-flex h-9 w-9 items-center justify-center rounded-pill border border-slate-200 text-slate-500 hover:bg-brand-600 hover:text-white hover:border-brand-600 transition-colors'
                                >
                                    {SOCIAL_ICON[s.platform] || <FaFacebookF />}
                                </a>
                            </li>
                        ))}
                    </ul>
                )}
            </aside>
        </header>
    )
}

export default Headers
